import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Phone, Menu, ChevronDown, ArrowRight,
  FileText,
} from "lucide-react";
import compassLogo from "@/assets/logo-header.webp";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { loanProductsByCategory } from "@/lib/loan-products";
import { serviceAreas } from "@/lib/service-areas";
import { Icon } from "@/components/Icon";

const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};
const iconFor = (slug: string) => PRODUCT_ICON_NAME[slug] ?? "dollarsign";

export type AgencyInfo = {
  name: string;
  short: string;
  phone: string;
  phoneHref: string;
  hours: string;
};

export function SiteHeader({ agency }: { agency: AgencyInfo }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3 lg:px-10">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          aria-label={agency.name}
        >
          <img
            src={compassLogo}
            alt={agency.name}
            width={500}
            height={167}
            className="h-10 w-auto md:h-12 object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList>
            {/* Business Loans */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>Business Loans</NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="grid w-[680px] grid-cols-3 gap-5 p-5">
                  {loanProductsByCategory.map((cat) => (
                    <div key={cat.id}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
                        {cat.label}
                      </p>
                      <ul className="space-y-0.5">
                        {cat.products.slice(0, 4).map((p) => (
                          <li key={p.slug}>
                            <Link
                              to="/dallas/$slug"
                              params={{ slug: p.slug }}
                              className="flex items-center gap-2 rounded-md px-2 py-1 text-[13px] text-foreground/85 transition hover:bg-secondary hover:text-foreground"
                            >
                              <Icon name={iconFor(p.slug)} color="co" className="h-4 w-4" />
                              <span>{p.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="col-span-3 border-t border-border pt-3">
                    <Link
                      to="/dallas"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
                    >
                      View all Dallas programs <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Service Areas */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>Service Areas</NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="w-[360px] p-6">
                  <a
                    href="/service-areas"
                    className="mb-3 block text-xs font-semibold uppercase tracking-[0.14em] text-accent hover:underline"
                  >
                    All Service Areas →
                  </a>
                  <ul className="grid grid-cols-2 gap-1">
                    {serviceAreas.map((a) => (
                      <li key={a.slug}>
                        <a
                          href={`/service-areas/${a.slug}`}
                          className="block rounded-md px-2 py-1.5 text-sm text-foreground/85 transition hover:bg-secondary hover:text-foreground"
                        >
                          {a.city}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        <div className="hidden items-center gap-6 lg:flex">
          <a
            href="/about"
            className="text-sm font-medium text-foreground/80 transition hover:text-accent"
          >
            About
          </a>
          <a
            href="/contact"
            className="text-sm font-medium text-foreground/80 transition hover:text-accent"
          >
            Contact
          </a>
        </div>

        {/* Contact block */}
        <div className="hidden items-center gap-4 lg:flex">
          <div className="text-right leading-tight">
            <a
              href={agency.phoneHref}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground transition hover:text-accent"
            >
              <Phone className="h-3.5 w-3.5" />
              {agency.phone}
            </a>
            <p className="text-[11px] text-muted-foreground">{agency.hours}</p>
          </div>
          <Button
            asChild
            size="sm"
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <a href={agency.phoneHref}>Call Now</a>
          </Button>
        </div>

        {/* Mobile trigger */}
        <div className="flex items-center gap-2 lg:hidden">
          <Button
            asChild
            size="sm"
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <a href={agency.phoneHref} aria-label="Call now">
              <Phone className="h-4 w-4" />
              Call
            </a>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
              <MobileNav agency={agency} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function MobileNav({
  agency,
  onNavigate,
}: {
  agency: AgencyInfo;
  onNavigate: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center"
          aria-label={agency.name}
        >
          <img
            src={compassLogo}
            alt={agency.name}
            width={200}
            height={60}
            className="h-10 w-auto object-contain"
          />
        </Link>
      </div>

      <div className="flex-1 px-6 py-6">
        <MobileSection label="Business Loans">
          <ul className="space-y-4">
            {loanProductsByCategory.map((cat) => (
              <li key={cat.id}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
                  {cat.label}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {cat.products.map((p) => (
                    <li key={p.slug}>
                      <Link
                        to="/dallas/$slug"
                        params={{ slug: p.slug }}
                        onClick={onNavigate}
                        className="flex items-center gap-2 text-sm text-foreground/85 transition hover:text-accent"
                      >
                        <Icon name={iconFor(p.slug)} color="co" className="h-4 w-4" />
                        <span>{p.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <Link
            to="/dallas"
            onClick={onNavigate}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent"
          >
            View all Dallas programs <ArrowRight className="h-4 w-4" />
          </Link>
        </MobileSection>

        <MobileSection label="Service Areas">
          <a
            href="/service-areas"
            onClick={onNavigate}
            className="mb-2 block text-sm font-semibold text-accent"
          >
            All Service Areas →
          </a>
          <ul className="grid grid-cols-2 gap-1.5">
            {serviceAreas.map((a) => (
              <li key={a.slug}>
                <a
                  href={`/service-areas/${a.slug}`}
                  onClick={onNavigate}
                  className="block text-sm text-foreground/85 transition hover:text-accent"
                >
                  {a.city}
                </a>
              </li>
            ))}
          </ul>
        </MobileSection>

        <div className="mt-6 grid gap-3 border-t border-border pt-6">
          <a
            href="/about"
            onClick={onNavigate}
            className="text-base font-semibold text-foreground transition hover:text-accent"
          >
            About
          </a>
          <a
            href="/contact"
            onClick={onNavigate}
            className="text-base font-semibold text-foreground transition hover:text-accent"
          >
            Contact
          </a>
        </div>
      </div>

      <div className="border-t border-border bg-secondary px-6 py-5">
        <a
          href={agency.phoneHref}
          className="flex items-center gap-2 text-base font-semibold text-foreground"
        >
          <Phone className="h-4 w-4 text-accent" />
          {agency.phone}
        </a>
        <p className="mt-0.5 text-xs text-muted-foreground">{agency.hours}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
            <a href={agency.phoneHref}>
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </Button>
          <SheetClose asChild>
            <Button asChild variant="outline">
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </SheetClose>
        </div>
      </div>
    </div>
  );
}

function MobileSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-border py-4 first:pt-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left text-base font-semibold text-foreground"
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}
